
<template>
  <Modal ref="modal">
    <div
      slot="content"
    >
      <p class="modal-pure-text">
        {{ $t('userManagement.inviteTip', [masterAccountFullName]) }}
      </p>
      <template v-if="needFloors">
        <p class="modal-pure-text">
          {{ $t('userManagement.selectFloorTip') }}
        </p>
        <div class="modal-pure-text tenancy-floor">
          <span class="required-flag">{{ $t('tenancy.floor') }}</span>:
          <div class="floor-select">
            <MasterInviteModalFloorSelect
              v-model="masterFloors"
              class="modal-select"
            />
          </div>
        </div>
      </template>
    </div>
    <div slot="btns">
      <button
        class="btn btn--darkblue"
        @click="close"
      >
        {{ $t('action.cancel') }}
      </button>
      <button
        class="btn btn--orange"
        @click="() => doInvite()"
      >
        {{ $t('action.invite') }}
      </button>
    </div>
  </Modal>
</template>

<script>
import lodash from 'lodash'
import KeyMirror from 'keymirror'
import { unref, computed, ref } from 'vue-demi'
import Modal from '@/controls/Modal'
import { useRefModal } from '@/controls/Modal/modal.hook'
import { useStoreGetters } from '@/store/index'
import { fusionInputCheckUtil } from '@/plugin/FusionInputCheck'
import { displayI18nFullName } from '@/utils/fullName'
import { getInactiveFloorsInTenancy, getActiveAndFutureFloors } from '../../modules/ccd/tenancy/utils'
import MasterInviteModalFloorSelect from './MasterInviteModalFloorSelect.vue'

const EVENTS = KeyMirror({
  inviteClick: null,
})

/**
 * @type {import('vue').Component}
 */
const MasterInviteModal = {
  props: {
    masterAccount: {
      type: Object,
      default: undefined,
    },
  },
  components: {
    Modal,
    MasterInviteModalFloorSelect,
  },
  setup (props, { emit }) {
    const { currentTenancy } = useStoreGetters('ccd', ['currentTenancy'])
    const modalRef = useRefModal()
    const masterFloors = ref([])
    const masterAccountFullName = computed(() => {
      const { masterAccount } = props
      const [firstName, lastName] = [
        lodash.get(unref(masterAccount), 'TenancyAccount.contactFirstName', ''),
        lodash.get(unref(masterAccount), 'TenancyAccount.contactLastName', ''),
      ]
      return displayI18nFullName(firstName, lastName)
    })

    const expiredFloors = computed(() => {
      return getInactiveFloorsInTenancy(unref(currentTenancy))
    })
    const eCircleContacts = computed(() => {
      return (unref(currentTenancy).tenancyContacts || [])
        .filter(item => lodash.get(item, 'CompanyContact.isCircular'))
        .map(item => {
          item.isExpired = item.CompanyContact.cFloor && item.CompanyContact.cFloor.every(floor => unref(expiredFloors).includes(floor))
          item.displayName = item.firstName && item.lastName ? displayI18nFullName(item.firstName, item.lastName) : item.name
          return item
        })
    })
    const allContacts = computed(() => {
      return (unref(currentTenancy).tenancyContacts || [])
        .filter(item => lodash.get(item, 'CompanyContact.isOthers'))
        .map((item) => {
          item.displayName = item.firstName && item.lastName ? displayI18nFullName(item.firstName, item.lastName) : item.name
          return item
        })
    })
    const isInECircleContacts = computed(() => {
      const curMasterDisplayName = unref(masterAccountFullName)
      const curMasterEmail = unref(props.masterAccount).username
      return unref(eCircleContacts).some(item => {
        return item.displayName === curMasterDisplayName && lodash.get(item, 'CompanyContact.cEmails', []).includes(curMasterEmail)
      })
    })
    const hasPremisesDetailList = computed(() => {
      return lodash.get(unref(currentTenancy), 'premisesDetailList.length', 0) > 0
    })
    const needFloors = computed(() => {
      if (!unref(hasPremisesDetailList)) return false
      if (unref(isInECircleContacts)) return false
      return true
    })
    return {
      masterFloors,
      masterAccountFullName,
      currentTenancy,
      expiredFloors,
      eCircleContacts,
      allContacts,
      hasPremisesDetailList,
      isInECircleContacts,
      needFloors,
      ...modalRef,
      open () {
        modalRef.open()
        masterFloors.value = getActiveAndFutureFloors(unref(currentTenancy))
        fusionInputCheckUtil.resetAllFields(unref(modalRef.modal).$el)
      },
      close () {
        fusionInputCheckUtil.resetAllFields(unref(modalRef.modal).$el)
        modalRef.close()
      },
      doInvite () {
        const nextData = {
          masterFloors: unref(masterFloors),
        }
        if (unref(needFloors)) {
          const el = unref(modalRef.modal).$el
          fusionInputCheckUtil.resetAllFields(el)
          fusionInputCheckUtil.validateAllFields(undefined, el)
          const isValidate = fusionInputCheckUtil.isAllFieldsValid(el)
          if (!isValidate) return
        } else {
          nextData.masterFloors = []
        }
        emit(EVENTS.inviteClick, nextData)
      },
    }
  },
}

export default MasterInviteModal
</script>
<style>
.tenancy-floor {
  display: flex;
  align-items: center;
}
.floor-select {
  margin-left: 3px;
}
</style>
